import * as React from "react";
import {Status, Wrapper} from "@googlemaps/react-wrapper";
import PropTypes from "prop-types";

import {GoogleMapApiKey, GoogleMapLibraries} from "../../config";
import {debounce} from "../../utils/debounce";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Typography from "@mui/material/Typography";


const GoogleAddressAutocomplete = ({onAddressChange = f => f}) => {
    return <Wrapper apiKey={GoogleMapApiKey} libraries={GoogleMapLibraries} render={(status) => {
        switch (status) {
            case Status.LOADING:
                return "Caricamento...";
            case Status.SUCCESS:
                return <GoogleAutocomplete autocompleteService={new window.google.maps.places.AutocompleteService()}
                                           geocoder={new window.google.maps.Geocoder()}
                                           onAddressChange={onAddressChange}
                />
            default:
                return "Errore caricamento servizi mappa";
        }
    }}/>
}

const GoogleAutocomplete = (props) => {
    const {autocompleteService, geocoder, onAddressChange = f => f} = props

    const [value, setValue] = React.useState(null);
    const [inputValue, setInputValue] = React.useState('');
    const [options, setOptions] = React.useState([]);

    // Fetch function that calls the autocomplete API of google to get a bunch of place and place id
    const fetchPlace = React.useMemo(
        () => debounce((request, callback) => autocompleteService.getPlacePredictions(request, callback), 500),
        [autocompleteService],
    );

    // Fetch function that calls the geocode APi to get the address from a place id
    const fetchPlaceIDAddress = React.useCallback(
        (request, callback) => geocoder.geocode(request, callback),
        [geocoder],
    );

    // Whenever the input change fetch google autocomplete and set the new options
    React.useEffect(() => {
        let active = true;

        if (inputValue === '') {
            setOptions(value ? [value] : []);
            return undefined;
        }

        fetchPlace({input: inputValue}, (results) => {
            if (active) {
                let newOptions = [];

                if (value) {
                    newOptions = [value];
                }

                if (results) {
                    newOptions = [...newOptions, ...results];
                }

                setOptions(newOptions);
            }
        });

        return () => {
            active = false;
        };
    }, [value, inputValue, fetchPlace]);

    // Handle a new selection click
    const onSelect = (event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        if (newValue && newValue.place_id) {
            fetchPlaceIDAddress({placeId: newValue['place_id']}, (result) => {
                if (result && result.length > 0) {
                    const addressComponents = result[0].address_components
                    const address = {
                        streetNumber: addressComponents.reduce((acc, i) => i.types.includes("street_number") ? i.long_name : acc, null),
                        street: addressComponents.reduce((acc, i) => i.types.includes("route") ? i.long_name : acc, null),
                        city: addressComponents.reduce((acc, i) => i.types.includes("locality") || i.types.includes("administrative_area_level_3") ? i.long_name : acc, null),
                        zipCode: addressComponents.reduce((acc, i) => i.types.includes("postal_code") ? i.long_name : acc, null),
                        country: addressComponents.reduce((acc, i) => i.types.includes("country") ? i.long_name : acc, null),
                    }
                    onAddressChange(address)
                }
            })
        }
    }

    return <Autocomplete
        id="google-map-address"
        fullWidth
        getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.description
        }
        noOptionsText="Nessun risultato"
        filterOptions={(x) => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={value}
        onChange={onSelect}
        sx={{minHeight: "100px"}}
        onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
        }}
        renderInput={(params) => (
            <TextField {...params} label="Indirizzo (via, numero civico, luogo)" fullWidth/>
        )}
        renderOption={(props, option) => {
            return (
                <li {...props}>
                    <Grid container alignItems="center">
                        <Grid item>
                            <Box
                                component={LocationOnIcon}
                                sx={{color: 'text.primary', mr: 2}}
                            />
                        </Grid>
                        <Grid item xs>
                            <Typography>
                                {option.description}
                            </Typography>
                        </Grid>
                    </Grid>
                </li>
            );
        }}
    />;
}

GoogleAddressAutocomplete.propTypes = {
    onAddressChange: PropTypes.func.isRequired
}

export default GoogleAddressAutocomplete;